import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { ButtonGroup, ButtonToolbar, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type { UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import ActionButton from "../components/ActionButton";
import { useErrorMessage } from "../components/FormError";
import IndeterminateCheckbox from "../components/IndeterminateCheckbox";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, useTable } from "../components/Table/Table";
import { authenticatedFetch } from "../lib/api";
import { formatPercent } from "../lib/numbers";
import BillingNav from "./BillingNav";
import {
  DELETE_ASSET_EXCLUSIONS_MUTATION_KEY,
  type SerializedAssetExclusionListItem,
  useQueryAssetExclusions,
} from "./lib";

type AssetExclusionRow = SerializedAssetExclusionListItem;

const columnHelper = createColumnHelper<AssetExclusionRow>();

const columns = [
  columnHelper.display({
    id: "select",
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
  }),
  columnHelper.accessor((row) => row.name, {
    id: "name",
    cell: (info) => (
      <Link to={`/billing/asset-exclusion/${info.row.original.id}`}>
        {info.getValue()}
      </Link>
    ),
    header: () => "Name",
  }),
  columnHelper.accessor((row) => row.weight, {
    id: "weight",
    cell: (info) => formatPercent(+info.getValue() / 100, 2),
    header: () => "Weight",
    enableColumnFilter: false,
  }),
  columnHelper.accessor((row) => row.accounts?.length ?? 0, {
    id: "noOfAccounts",
    header: () => "# of Accounts",
    enableColumnFilter: false,
    meta: {
      className: "text-end",
      headerClassName: "text-end",
    },
  }),
  columnHelper.accessor((row) => row.securities?.length ?? 0, {
    id: "noOfSecurities",
    header: () => "# of Securities",
    enableColumnFilter: false,
    meta: {
      className: "text-end",
      headerClassName: "text-end",
    },
  }),
];

const AssetExclusionTable = () => {
  const { isPending, data } = useQueryAssetExclusions();

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: data?.data ?? [],
  });

  const { getAccessTokenSilently } = useAuth0();

  const { setError, resetError, errorMessage } = useErrorMessage();

  // One `DELETE /v1/billing/asset-exclusions/:id` per selected row (APP-1651:
  // the bulk `POST /billing/asset-exclusions/delete-many` is removed).
  const removeAssetExclusions = (ids: number[]) =>
    Promise.allSettled(
      ids.map((id) =>
        authenticatedFetch<
          UnpackResponse<BillingController["deleteAssetExclusion"]>
        >(
          `${process.env.REACT_APP_API_ROOT}/v1/billing/asset-exclusions/${id}`,
          getAccessTokenSilently,
          { method: "DELETE" },
        )(),
      ),
    );

  // `useQueryAssetExclusions` walks the list again when this settles
  // (`DELETE_ASSET_EXCLUSIONS_MUTATION_KEY`), after a partial failure too: a
  // failed delete does not make it settle as an error. The selection is
  // cleared then as well: it holds row indexes, and the rows that are left take
  // new indexes when the list is read again.
  const del = useMutation({
    mutationKey: DELETE_ASSET_EXCLUSIONS_MUTATION_KEY,
    mutationFn: async () => {
      if (Object.keys(rowSelection).length === 0) return;
      resetError();
      const results = await removeAssetExclusions(
        data?.data.reduce((result, assetExclusion, idx) => {
          return Object.keys(rowSelection).includes(`${idx}`)
            ? [...result, assetExclusion.id]
            : result;
        }, [] as number[]) ?? [],
      );
      setRowSelection({});
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length > 0) {
        const message = "Failed to delete asset exclusions.";
        setError(message);
        console.error(message, failed);
      }
    },
  });

  return (
    <TabContainerWithTabs tabs={BillingNav}>
      {errorMessage}
      <Row>
        <Col className="d-flex justify-content-between mb-3">
          <ButtonToolbar className="gap-3">
            <ButtonGroup>
              <ActionButton
                as={Link}
                to="new"
                variant="secondary"
                label="Create"
                icon="/icons/new.svg"
              />
            </ButtonGroup>
            <ButtonGroup>
              <ActionButton
                variant="secondary"
                label="Delete"
                icon="/icons/trash.svg"
                onClick={() => del.mutate()}
                disabled={
                  del.isPending || Object.keys(rowSelection).length === 0
                }
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>{isPending ? <Loading /> : <Table table={table} />}</Col>
      </Row>
    </TabContainerWithTabs>
  );
};

export default AssetExclusionTable;
